import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Tabs } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Check, ChevronDown, Info } from 'lucide-react';
import EmptyState from 'components/EmptyState';
import { cn } from 'lib/utils';
import type { TechnicianImportValidateResponse } from 'api/technicianImportContract';
import { exclusionCount, ignoredRowCount } from '../importLabels';
import ImportErrorsPanel from './ImportErrorsPanel';
import ImportAdditionsPanel from './ImportAdditionsPanel';
import ImportChangesPanel from './ImportChangesPanel';
import ImportExclusionsPanel from './ImportExclusionsPanel';

const TABS = ['errors', 'additions', 'changes', 'exclusions'] as const;
type Tab = (typeof TABS)[number];

/** CA6–CA10: the four tabs of what the file would do; nothing here writes. */
const ImportPreview = ({ preview }: { preview: TechnicianImportValidateResponse }) => {
  const { t } = useTranslation();
  const { counts } = preview;
  const tabCount: Record<Tab, number> = {
    errors: counts.errors + counts.warnings,
    additions: counts.additions,
    changes: counts.changes,
    exclusions: exclusionCount(preview),
  };
  const firstFilled = TABS.find((tab) => tabCount[tab] > 0) ?? null;
  const [tab, setTab] = useState<Tab | null>(firstFilled);
  const [absentOpen, setAbsentOpen] = useState(false);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  // CA7: the first non-empty tab takes the focus once the analysis lands.
  useEffect(() => {
    if (firstFilled) tabRefs.current[firstFilled]?.focus();
  }, [firstFilled]);

  const label: Record<Tab, string> = {
    errors: t('To correct'),
    additions: t('Additions'),
    changes: t('Changes'),
    exclusions: t('Bans'),
  };

  const panels: Record<Tab, ReactNode> = {
    errors: <ImportErrorsPanel errors={preview.errors} />,
    additions: <ImportAdditionsPanel additions={preview.additions} />,
    changes: <ImportChangesPanel changes={preview.changes} />,
    exclusions: <ImportExclusionsPanel preview={preview} />,
  };

  const errorTerm = t('{{count}} errors', { count: counts.errors });
  const warningTerm = t('{{count}} warnings', { count: counts.warnings });
  const errorsAccessibleCount =
    counts.errors && counts.warnings
      ? t('{{errors}} and {{warnings}}', { errors: errorTerm, warnings: warningTerm })
      : counts.warnings ? warningTerm : counts.errors ? errorTerm : '0';
  const tone = counts.errors > 0 ? 'error' : counts.warnings > 0 ? 'warning' : 'neutral';

  const erasures = preview.changes.reduce((sum, change) => sum + change.fields.filter((field) => field.erased).length, 0);
  const technicians = (count: number) => t('{{count}} technicians', { count });
  const fields = t('{{count}} fields', { count: preview.changes.reduce((sum, change) => sum + change.fields.length, 0) });
  const summary: Record<Tab, string> = {
    errors: counts.warnings
      ? t('{{ignored}} · {{warnings}}', { ignored: t('{{count}} rows ignored', { count: ignoredRowCount(preview) }), warnings: warningTerm })
      : t('{{count}} rows ignored', { count: ignoredRowCount(preview) }),
    additions: technicians(counts.additions),
    changes: erasures
      ? t('{{technicians}} · {{fields}} · {{erasures}}', {
          technicians: technicians(counts.changes),
          fields,
          erasures: t('of which {{count}} erasures', { count: erasures }),
        })
      : t('{{technicians}} · {{fields}}', { technicians: technicians(counts.changes), fields }),
    exclusions: technicians(tabCount.exclusions),
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5">
      {!preview.geocodingChecked && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('The addresses were not checked.')}
        </p>
      )}
      {counts.absent > 0 && (
        <div className="rounded-lg bg-muted px-3 py-2 text-[13px]">
          <div className="flex items-center justify-between gap-2.5">
            <span>{t('{{count}} technicians of the base absent from the file — unchanged', { count: counts.absent })}</span>
            <button
              type="button"
              aria-expanded={absentOpen}
              onClick={() => setAbsentOpen((open) => !open)}
              className="inline-flex items-center gap-1 whitespace-nowrap font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {absentOpen ? t('Hide the list') : t('See the list')}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', absentOpen && 'rotate-180')} aria-hidden="true" />
            </button>
          </div>
          {absentOpen && (
            <ul className="mt-2 grid max-h-[140px] grid-cols-3 overflow-y-auto text-foreground/80">
              {preview.absent.map((technician) => <li key={technician.userId}>{technician.name}</li>)}
            </ul>
          )}
        </div>
      )}

      <Tabs.Root value={tab ?? ''} onValueChange={(next) => setTab(next as Tab)} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <Tabs.List aria-label={t('Import preview')} className="inline-flex gap-1 rounded-xl bg-muted p-1 !shadow-none">
            {TABS.map((id) => {
              const toneOf = id === 'errors' ? tone : 'neutral';
              const Icon = toneOf === 'error' ? AlertCircle : AlertTriangle;
              return (
                <Tabs.Trigger
                  key={id}
                  value={id}
                  ref={(node) => { tabRefs.current[id] = node; }}
                  aria-label={`${label[id]}, ${id === 'errors' ? errorsAccessibleCount : tabCount[id]}`}
                  // `!`: the Themes sheet loads after Tailwind; its underline and inner padding are dropped.
                  className={cn(
                    'group !h-8 rounded-lg !px-3 !text-[13px] before:!hidden [&>span]:!bg-transparent [&>span]:!p-0',
                    'data-[state=active]:bg-background data-[state=active]:font-semibold data-[state=active]:shadow-sm dark:data-[state=active]:bg-zinc-800',
                    toneOf === 'error' && '!text-rose-700 dark:!text-rose-400',
                    toneOf === 'warning' && '!text-amber-800 dark:!text-amber-300',
                    toneOf === 'neutral' && '!text-zinc-600 dark:!text-zinc-400 data-[state=active]:!text-foreground'
                  )}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {toneOf !== 'neutral' && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
                    {label[id]}
                    <span
                      className={cn(
                        'rounded-md px-1.5 text-[11px] font-semibold tabular-nums',
                        toneOf === 'error' && 'bg-rose-100 dark:bg-rose-950/60',
                        toneOf === 'warning' && 'bg-amber-100 dark:bg-amber-950/60',
                        toneOf === 'neutral' && 'bg-zinc-200/70 text-zinc-600 group-data-[state=active]:bg-muted dark:bg-zinc-700/60 dark:text-zinc-400 dark:group-data-[state=active]:bg-zinc-900'
                      )}
                    >
                      {tabCount[id]}
                    </span>
                  </span>
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>
          {tab && <p className="text-[12.5px] text-muted-foreground">{summary[tab]}</p>}
        </div>
        {TABS.map((id) => (
          <Tabs.Content key={id} value={id} className="min-h-0 flex-1 overflow-y-auto pt-2.5">
            {panels[id]}
          </Tabs.Content>
        ))}
      </Tabs.Root>

      {!tab && (
        // M8: everything at 0, the file already matches the base
        <EmptyState
          tone="success"
          icon={Check}
          title={t('The file already matches the base: {{count}} technicians unchanged.', { count: counts.unchanged })}
        />
      )}
    </div>
  );
};

export default ImportPreview;
